import { useEffect, useState, useCallback } from 'react';
import {
    Building,
    Truck,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    Search,
    Filter,
    RefreshCw,
    Eye,
    Check,
    X,
    Play,
    CreditCard,
    FileText,
    ChevronDown,
    Loader2,
    DollarSign,
    ArrowRight,
    Calendar,
    Building2,
    Phone,
    Mail,
    Wallet,
    Download,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { StatusBadge } from '../../components/ui/status-badge';
import { Input } from '../../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { WithdrawalsAPI, WithdrawalRequest, WithdrawalsResponse } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';
import { toast } from 'sonner';
import { WithdrawalKanbanBoard } from '../../components/earnings/WithdrawalKanbanBoard';

type StatusFilter = 'all' | 'pending' | 'approved' | 'processing' | 'completed' | 'rejected';
type EntityFilter = 'all' | 'supplier' | 'transporter';

const statusConfig: Record<string, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
    pending: { label: 'Pending', color: 'text-amber-700', bgColor: 'bg-amber-50 border-amber-200', icon: <Clock className="h-4 w-4" /> },
    approved: { label: 'Approved', color: 'text-blue-700', bgColor: 'bg-blue-50 border-blue-200', icon: <CheckCircle className="h-4 w-4" /> },
    processing: { label: 'Processing', color: 'text-purple-700', bgColor: 'bg-purple-50 border-purple-200', icon: <Play className="h-4 w-4" /> },
    completed: { label: 'Completed', color: 'text-green-700', bgColor: 'bg-green-50 border-green-200', icon: <CheckCircle className="h-4 w-4" /> },
    rejected: { label: 'Rejected', color: 'text-red-700', bgColor: 'bg-red-50 border-red-200', icon: <XCircle className="h-4 w-4" /> },
    cancelled: { label: 'Cancelled', color: 'text-gray-700', bgColor: 'bg-gray-50 border-gray-200', icon: <X className="h-4 w-4" /> },
};

export default function WithdrawalsPage() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<WithdrawalsResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
    const [entityFilter, setEntityFilter] = useState<EntityFilter>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');

    // Modals
    const [selectedWithdrawal, setSelectedWithdrawal] = useState<WithdrawalRequest | null>(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);

    // Form state
    const [actionNotes, setActionNotes] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [transactionId, setTransactionId] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<'bank_transfer' | 'upi' | 'neft' | 'rtgs' | 'imps'>('neft');
    const [bankReference, setBankReference] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const fetchWithdrawals = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await WithdrawalsAPI.getAllWithdrawals({
                page,
                limit: 20,
                status: statusFilter === 'all' ? undefined : statusFilter,
                entityType: entityFilter === 'all' ? undefined : entityFilter,
                search: searchQuery || undefined,
                sortBy: 'requestedAt',
                sortOrder: 'asc',
            });

            if (response.success) {
                setData(response.data);
            } else {
                setError(response.message || 'Failed to fetch withdrawals');
            }
        } catch (err: any) {
            console.error('Error fetching withdrawals:', err);
            setError(err.message || 'Failed to fetch withdrawals');
        } finally {
            setLoading(false);
        }
    }, [page, statusFilter, entityFilter, searchQuery]);

    useEffect(() => {
        fetchWithdrawals();
    }, [fetchWithdrawals]);

    const handleSearch = () => {
        setPage(1);
        fetchWithdrawals();
    };

    const resetFilters = () => {
        setStatusFilter('pending');
        setEntityFilter('all');
        setSearchQuery('');
        setPage(1);
    };

    // Action handlers
    const handleApprove = async () => {
        if (!selectedWithdrawal) return;

        try {
            setActionLoading(true);
            const response = await WithdrawalsAPI.approveWithdrawal(selectedWithdrawal._id, actionNotes);

            if (response.success) {
                toast.success('Withdrawal approved successfully');
                setShowApproveModal(false);
                setActionNotes('');
                fetchWithdrawals();
            } else {
                toast.error(response.message || 'Failed to approve withdrawal');
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to approve withdrawal');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async () => {
        if (!selectedWithdrawal || !rejectReason.trim()) {
            toast.error('Please provide a rejection reason');
            return;
        }

        try {
            setActionLoading(true);
            const response = await WithdrawalsAPI.rejectWithdrawal(selectedWithdrawal._id, rejectReason);

            if (response.success) {
                toast.success('Withdrawal rejected');
                setShowRejectModal(false);
                setRejectReason('');
                fetchWithdrawals();
            } else {
                toast.error(response.message || 'Failed to reject withdrawal');
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to reject withdrawal');
        } finally {
            setActionLoading(false);
        }
    };

    const handleProcess = async () => {
        if (!selectedWithdrawal) return;

        try {
            setActionLoading(true);
            const response = await WithdrawalsAPI.processWithdrawal(selectedWithdrawal._id, actionNotes);

            if (response.success) {
                toast.success('Withdrawal processing started');
                setShowProcessModal(false);
                setActionNotes('');
                fetchWithdrawals();
            } else {
                toast.error(response.message || 'Failed to start processing');
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to start processing');
        } finally {
            setActionLoading(false);
        }
    };

    const handleComplete = async () => {
        if (!selectedWithdrawal || !transactionId.trim()) {
            toast.error('Please provide a transaction ID');
            return;
        }

        try {
            setActionLoading(true);
            const response = await WithdrawalsAPI.completeWithdrawal(selectedWithdrawal._id, {
                transactionId,
                paymentMethod,
                bankReference: bankReference || undefined,
                notes: actionNotes || undefined,
            });

            if (response.success) {
                toast.success('Withdrawal completed successfully');
                setShowCompleteModal(false);
                setTransactionId('');
                setBankReference('');
                setActionNotes('');
                fetchWithdrawals();
            } else {
                toast.error(response.message || 'Failed to complete withdrawal');
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to complete withdrawal');
        } finally {
            setActionLoading(false);
        }
    };

    const openActionModal = (withdrawal: WithdrawalRequest, action: 'approve' | 'reject' | 'process' | 'complete' | 'view') => {
        setSelectedWithdrawal(withdrawal);
        switch (action) {
            case 'view':
                setShowDetailModal(true);
                break;
            case 'approve':
                setShowApproveModal(true);
                break;
            case 'reject':
                setShowRejectModal(true);
                break;
            case 'process':
                setShowProcessModal(true);
                break;
            case 'complete':
                setShowCompleteModal(true);
                break;
        }
    };

    const stats = data?.stats || {
        pending: { count: 0, totalAmount: 0 },
        approved: { count: 0, totalAmount: 0 },
        processing: { count: 0, totalAmount: 0 },
        completed: { count: 0, totalAmount: 0 },
        rejected: { count: 0, totalAmount: 0 },
    };

    const getActionButtons = (withdrawal: WithdrawalRequest) => {
        switch (withdrawal.status) {
            case 'pending':
                return (
                    <>
                        <Button size="sm" variant="outline" onClick={() => openActionModal(withdrawal, 'view')}>
                            <Eye className="h-4 w-4 mr-1" /> View
                        </Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openActionModal(withdrawal, 'approve')}>
                            <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => openActionModal(withdrawal, 'reject')}>
                            <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                    </>
                );
            case 'approved':
                return (
                    <>
                        <Button size="sm" variant="outline" onClick={() => openActionModal(withdrawal, 'view')}>
                            <Eye className="h-4 w-4 mr-1" /> View
                        </Button>
                        <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => openActionModal(withdrawal, 'process')}>
                            <Play className="h-4 w-4 mr-1" /> Process
                        </Button>
                    </>
                );
            case 'processing':
                return (
                    <>
                        <Button size="sm" variant="outline" onClick={() => openActionModal(withdrawal, 'view')}>
                            <Eye className="h-4 w-4 mr-1" /> View
                        </Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => openActionModal(withdrawal, 'complete')}>
                            <CreditCard className="h-4 w-4 mr-1" /> Complete
                        </Button>
                    </>
                );
            default:
                return (
                    <Button size="sm" variant="outline" onClick={() => openActionModal(withdrawal, 'view')}>
                        <Eye className="h-4 w-4 mr-1" /> View Details
                    </Button>
                );
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-3">
                        <Wallet className="h-8 w-8 text-primary" />
                        Withdrawal Management
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Review and process withdrawal requests from suppliers and transporters
                    </p>
                </div>
                <div className="flex gap-2">
                    {/* View Toggle */}
                    <div className="flex rounded-lg border p-1 bg-muted/30">
                        <Button
                            variant={viewMode === 'list' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('list')}
                            className="gap-1.5"
                        >
                            <FileText className="h-4 w-4" />
                            List
                        </Button>
                        <Button
                            variant={viewMode === 'kanban' ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('kanban')}
                            className="gap-1.5"
                        >
                            <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
                            Kanban
                        </Button>
                    </div>
                    <Button variant="outline" onClick={fetchWithdrawals} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    <Button variant="outline">
                        <Download className="h-4 w-4 mr-2" />
                        Export
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {Object.entries(stats).map(([status, stat]) => {
                    const config = statusConfig[status];
                    if (!config) return null;
                    return (
                        <Card
                            key={status}
                            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === status ? 'ring-2 ring-primary' : ''}`}
                            onClick={() => setStatusFilter(status as StatusFilter)}
                        >
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                                    <div className={`p-1.5 rounded-full ${config.bgColor}`}>
                                        {config.icon}
                                    </div>
                                </div>
                                <p className="text-2xl font-bold">{stat.count}</p>
                                <p className="text-sm text-muted-foreground">{formatCurrency(stat.totalAmount)}</p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row gap-4 items-end">
                        <div className="flex-1">
                            <Label className="text-sm text-muted-foreground mb-1.5 block">Search</Label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Search by Request ID or Entity Name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                    className="pl-10"
                                />
                            </div>
                        </div>

                        <div className="w-full md:w-48">
                            <Label className="text-sm text-muted-foreground mb-1.5 block">Status</Label>
                            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Filter by status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Statuses</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="approved">Approved</SelectItem>
                                    <SelectItem value="processing">Processing</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                    <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="w-full md:w-48">
                            <Label className="text-sm text-muted-foreground mb-1.5 block">Entity Type</Label>
                            <Select value={entityFilter} onValueChange={(v) => setEntityFilter(v as EntityFilter)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Filter by type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Types</SelectItem>
                                    <SelectItem value="supplier">Suppliers</SelectItem>
                                    <SelectItem value="transporter">Transporters</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <Button onClick={handleSearch}>
                            <Filter className="h-4 w-4 mr-2" />
                            Apply
                        </Button>

                        <Button variant="ghost" onClick={resetFilters}>
                            Reset
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Kanban View */}
            {viewMode === 'kanban' && (
                <WithdrawalKanbanBoard
                    withdrawals={data?.withdrawals || []}
                    onApprove={(w) => openActionModal(w, 'approve')}
                    onReject={(w) => openActionModal(w, 'reject')}
                    onProcess={(w) => openActionModal(w, 'process')}
                    onComplete={(w) => openActionModal(w, 'complete')}
                    onViewDetails={(w) => openActionModal(w, 'view')}
                    loading={loading}
                />
            )}

            {/* List View */}
            {viewMode === 'list' && (
                <Card>
                    <CardHeader>
                        <CardTitle>Withdrawal Requests</CardTitle>
                        <CardDescription>
                            {data?.pagination?.total || 0} total requests • Showing page {page} of {data?.pagination?.pages || 1}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                <span className="ml-3 text-muted-foreground">Loading withdrawals...</span>
                            </div>
                        ) : error ? (
                            <div className="text-center py-12">
                                <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
                                <p className="text-red-600 mb-4">{error}</p>
                                <Button onClick={fetchWithdrawals}>Try Again</Button>
                            </div>
                        ) : !data?.withdrawals?.length ? (
                            <div className="text-center py-12">
                                <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                <p className="text-muted-foreground">No withdrawal requests found</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {statusFilter !== 'all'
                                        ? `Try changing the status filter from "${statusFilter}"`
                                        : 'Withdrawal requests will appear here when submitted'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {data.withdrawals.map((withdrawal) => {
                                    const config = statusConfig[withdrawal.status] || statusConfig.pending;
                                    const isSupplier = withdrawal.entityType === 'supplier';

                                    return (
                                        <div
                                            key={withdrawal._id}
                                            className={`border rounded-lg p-4 transition-all hover:shadow-md ${config.bgColor}`}
                                        >
                                            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                                                {/* Entity Info */}
                                                <div className="flex items-start gap-3 flex-1">
                                                    <div className={`p-2 rounded-full ${isSupplier ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                                                        {isSupplier ? <Building className="h-5 w-5" /> : <Truck className="h-5 w-5" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <h3 className="font-semibold text-lg truncate">{withdrawal.entityName}</h3>
                                                            <Badge variant="outline" className={`text-xs ${isSupplier ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
                                                                {isSupplier ? 'Supplier' : 'Transporter'}
                                                            </Badge>
                                                            <StatusBadge status={withdrawal.status} label={config.label} />
                                                        </div>
                                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
                                                            <span className="flex items-center gap-1">
                                                                <Mail className="h-3.5 w-3.5" />
                                                                {withdrawal.entityEmail}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <Phone className="h-3.5 w-3.5" />
                                                                {withdrawal.entityMobile}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <FileText className="h-3.5 w-3.5" />
                                                                {withdrawal.requestId}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Amount & Bank */}
                                                <div className="flex flex-col items-end gap-1 min-w-[200px]">
                                                    <span className="text-2xl font-bold text-primary">{formatCurrency(withdrawal.amount)}</span>
                                                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                                                        <Building2 className="h-3.5 w-3.5" />
                                                        {withdrawal.bankDetails.bankName} • {withdrawal.bankDetails.accountNumberMasked}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                        <Calendar className="h-3 w-3" />
                                                        Requested: {new Date(withdrawal.requestedAt).toLocaleDateString()}
                                                    </span>
                                                </div>

                                                {/* Actions */}
                                                <div className="flex items-center gap-2 flex-wrap lg:flex-nowrap">
                                                    {getActionButtons(withdrawal)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Pagination */}
                        {data && data.pagination && data.pagination.pages > 1 && (
                            <div className="flex justify-center gap-2 mt-6">
                                <Button
                                    variant="outline"
                                    disabled={page === 1}
                                    onClick={() => setPage(p => p - 1)}
                                >
                                    Previous
                                </Button>
                                <span className="flex items-center px-4 text-sm text-muted-foreground">
                                    Page {page} of {data.pagination.pages}
                                </span>
                                <Button
                                    variant="outline"
                                    disabled={page === data.pagination.pages}
                                    onClick={() => setPage(p => p + 1)}
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Detail Modal */}
            <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Wallet className="h-5 w-5" />
                            Withdrawal Details
                        </DialogTitle>
                        <DialogDescription>
                            Request ID: {selectedWithdrawal?.requestId}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedWithdrawal && (
                        <div className="space-y-6">
                            {/* Status */}
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-muted-foreground">Status:</span>
                                <StatusBadge status={selectedWithdrawal.status} label={statusConfig[selectedWithdrawal.status]?.label} />
                            </div>

                            {/* Entity Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Entity</Label>
                                    <p className="font-medium">{selectedWithdrawal.entityName}</p>
                                    <p className="text-sm text-muted-foreground capitalize">{selectedWithdrawal.entityType}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Amount</Label>
                                    <p className="text-2xl font-bold text-primary">{formatCurrency(selectedWithdrawal.amount)}</p>
                                </div>
                            </div>

                            {/* Contact */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-muted-foreground">Email</Label>
                                    <p className="font-medium">{selectedWithdrawal.entityEmail}</p>
                                </div>
                                <div>
                                    <Label className="text-muted-foreground">Phone</Label>
                                    <p className="font-medium">{selectedWithdrawal.entityMobile}</p>
                                </div>
                            </div>

                            {/* Bank Details */}
                            <div className="bg-muted/50 rounded-lg p-4">
                                <Label className="text-muted-foreground mb-2 block">Bank Details</Label>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-muted-foreground">Account Holder:</span>
                                        <p className="font-medium">{selectedWithdrawal.bankDetails.accountHolderName}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Bank:</span>
                                        <p className="font-medium">{selectedWithdrawal.bankDetails.bankName}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">Account Number:</span>
                                        <p className="font-medium">{selectedWithdrawal.bankDetails.accountNumberMasked}</p>
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">IFSC Code:</span>
                                        <p className="font-medium">{selectedWithdrawal.bankDetails.ifscCode}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Timestamps */}
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Requested At:</span>
                                    <span>{new Date(selectedWithdrawal.requestedAt).toLocaleString()}</span>
                                </div>
                                {selectedWithdrawal.approvedAt && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Approved At:</span>
                                        <span>{new Date(selectedWithdrawal.approvedAt).toLocaleString()}</span>
                                    </div>
                                )}
                                {selectedWithdrawal.processedAt && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Processing Started:</span>
                                        <span>{new Date(selectedWithdrawal.processedAt).toLocaleString()}</span>
                                    </div>
                                )}
                                {selectedWithdrawal.completedAt && (
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Completed At:</span>
                                        <span>{new Date(selectedWithdrawal.completedAt).toLocaleString()}</span>
                                    </div>
                                )}
                                {selectedWithdrawal.rejectedAt && (
                                    <div className="flex justify-between text-red-600">
                                        <span>Rejected At:</span>
                                        <span>{new Date(selectedWithdrawal.rejectedAt).toLocaleString()}</span>
                                    </div>
                                )}
                            </div>

                            {/* Rejection Reason */}
                            {selectedWithdrawal.rejectionReason && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <Label className="text-red-700 mb-1 block">Rejection Reason</Label>
                                    <p className="text-red-600">{selectedWithdrawal.rejectionReason}</p>
                                </div>
                            )}

                            {/* Payment Details */}
                            {selectedWithdrawal.paymentDetails && (
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <Label className="text-green-700 mb-2 block">Payment Details</Label>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-muted-foreground">Transaction ID:</span>
                                            <p className="font-medium">{selectedWithdrawal.paymentDetails.transactionId}</p>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Method:</span>
                                            <p className="font-medium uppercase">{selectedWithdrawal.paymentDetails.paymentMethod}</p>
                                        </div>
                                        <div>
                                            <span className="text-muted-foreground">Amount Paid:</span>
                                            <p className="font-medium">{formatCurrency(selectedWithdrawal.paymentDetails.paidAmount)}</p>
                                        </div>
                                        {selectedWithdrawal.paymentDetails.bankReference && (
                                            <div>
                                                <span className="text-muted-foreground">Bank Reference:</span>
                                                <p className="font-medium">{selectedWithdrawal.paymentDetails.bankReference}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Admin Notes */}
                            {selectedWithdrawal.adminNotes && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <Label className="text-blue-700 mb-1 block">Admin Notes</Label>
                                    <p className="text-blue-600">{selectedWithdrawal.adminNotes}</p>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Approve Modal */}
            <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-green-600">
                            <CheckCircle className="h-5 w-5" />
                            Approve Withdrawal
                        </DialogTitle>
                        <DialogDescription>
                            Approve this withdrawal request for {selectedWithdrawal?.entityName}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                            <p className="text-sm text-muted-foreground">Amount to be approved</p>
                            <p className="text-3xl font-bold text-green-600">{formatCurrency(selectedWithdrawal?.amount || 0)}</p>
                        </div>

                        <div>
                            <Label>Notes (Optional)</Label>
                            <Textarea
                                placeholder="Add any notes about this approval..."
                                value={actionNotes}
                                onChange={(e) => setActionNotes(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowApproveModal(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button className="bg-green-600 hover:bg-green-700" onClick={handleApprove} disabled={actionLoading}>
                            {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                            Approve
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject Modal */}
            <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <XCircle className="h-5 w-5" />
                            Reject Withdrawal
                        </DialogTitle>
                        <DialogDescription>
                            Reject this withdrawal request for {selectedWithdrawal?.entityName}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                            <p className="text-sm text-muted-foreground">Amount to be rejected</p>
                            <p className="text-3xl font-bold text-red-600">{formatCurrency(selectedWithdrawal?.amount || 0)}</p>
                        </div>

                        <div>
                            <Label>Rejection Reason (Required)</Label>
                            <Textarea
                                placeholder="Provide a clear reason for rejection..."
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                rows={3}
                                className="border-red-200 focus:border-red-400"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRejectModal(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleReject} disabled={actionLoading || !rejectReason.trim()}>
                            {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <X className="h-4 w-4 mr-2" />}
                            Reject
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Process Modal */}
            <Dialog open={showProcessModal} onOpenChange={setShowProcessModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-purple-600">
                            <Play className="h-5 w-5" />
                            Start Processing
                        </DialogTitle>
                        <DialogDescription>
                            Mark this withdrawal as processing for {selectedWithdrawal?.entityName}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                            <p className="text-sm text-muted-foreground">Amount to process</p>
                            <p className="text-3xl font-bold text-purple-600">{formatCurrency(selectedWithdrawal?.amount || 0)}</p>
                        </div>

                        <div>
                            <Label>Notes (Optional)</Label>
                            <Textarea
                                placeholder="Add notes about processing..."
                                value={actionNotes}
                                onChange={(e) => setActionNotes(e.target.value)}
                                rows={3}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowProcessModal(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleProcess} disabled={actionLoading}>
                            {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                            Start Processing
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Complete Modal */}
            <Dialog open={showCompleteModal} onOpenChange={setShowCompleteModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-green-600">
                            <CreditCard className="h-5 w-5" />
                            Complete Withdrawal
                        </DialogTitle>
                        <DialogDescription>
                            Mark this withdrawal as completed for {selectedWithdrawal?.entityName}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                            <p className="text-sm text-muted-foreground">Amount to pay</p>
                            <p className="text-3xl font-bold text-green-600">{formatCurrency(selectedWithdrawal?.amount || 0)}</p>
                        </div>

                        <div>
                            <Label>Transaction ID (Required)</Label>
                            <Input
                                placeholder="Enter bank transaction ID..."
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                            />
                        </div>

                        <div>
                            <Label>Payment Method</Label>
                            <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v as any)}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="neft">NEFT</SelectItem>
                                    <SelectItem value="rtgs">RTGS</SelectItem>
                                    <SelectItem value="imps">IMPS</SelectItem>
                                    <SelectItem value="upi">UPI</SelectItem>
                                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label>Bank Reference (Optional)</Label>
                            <Input
                                placeholder="Enter bank reference number..."
                                value={bankReference}
                                onChange={(e) => setBankReference(e.target.value)}
                            />
                        </div>

                        <div>
                            <Label>Notes (Optional)</Label>
                            <Textarea
                                placeholder="Add any notes..."
                                value={actionNotes}
                                onChange={(e) => setActionNotes(e.target.value)}
                                rows={2}
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCompleteModal(false)} disabled={actionLoading}>
                            Cancel
                        </Button>
                        <Button className="bg-green-600 hover:bg-green-700" onClick={handleComplete} disabled={actionLoading || !transactionId.trim()}>
                            {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                            Complete Payment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
